import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PublicNav } from "../components/public/PublicNav";
import { PublicFooter } from "../components/public/PublicFooter";

const RESTAURANT_NAME = "Demo Bistro";
const BOT_AVATAR = "C";

type Bubble = { from: "customer" | "bot"; text: string };

type DemoScenario = {
  id: string;
  label: string;
  tag: string;
  tagColor: string;
  bubbles: Bubble[];
};

const SCENARIOS: DemoScenario[] = [
  {
    id: "dine-in",
    label: "Dine-in",
    tag: "Table T3",
    tagColor: "bg-violet-100 text-violet-700 border-violet-200",
    bubbles: [
      {
        from: "bot",
        text: `Hey there! 👋 Welcome to **${RESTAURANT_NAME}**. You're at **Table 3** — what can I help you with today?`,
      },
      { from: "customer", text: "What's good here? I'm in the mood for something spicy" },
      {
        from: "bot",
        text: "Great question! For spicy starters, **Peri Bites** (6 pcs, PKR 550) are really popular. If you want a fuller main, **Dragon Chicken** (full, PKR 820) has a nice kick. Prefer a starter or a main?",
      },
      { from: "customer", text: "Starter sounds good — maybe not too heavy though" },
      {
        from: "bot",
        text: "Then **Peri Bites** is perfect — crispy, spicy, and easy to share. One serving is 6 pieces. Shall I add one serving?",
      },
      { from: "customer", text: "Yes please, one serving" },
      { from: "bot", text: "Done — one Peri Bites (PKR 550). Would you like a drink with that, or are we good?" },
      { from: "customer", text: "I'm fine, that's all" },
      {
        from: "bot",
        text: "Sounds good! Your total is **PKR 550**. Will you pay with **card** or **cash**? And what name should I put on the order?",
      },
      { from: "customer", text: "Cash — Ahmed" },
      {
        from: "bot",
        text: "Thanks Ahmed! ✅ I've sent this to the kitchen — one Peri Bites, cash, PKR 550. We'll bring it to your table shortly.",
      },
    ],
  },
  {
    id: "delivery",
    label: "Delivery",
    tag: "Home delivery",
    tagColor: "bg-sky-100 text-sky-700 border-sky-200",
    bubbles: [
      {
        from: "bot",
        text: `Welcome to **${RESTAURANT_NAME}**! 👋 Are you looking to browse the menu, or do you already know what you'd like?`,
      },
      { from: "customer", text: "Not sure yet — any combo you'd recommend?" },
      {
        from: "bot",
        text: "A lot of people go for **Dragon Chicken** (full, PKR 820) with a **7up** (PKR 120) — comes to PKR 940 before delivery. Want that, or should I suggest something milder?",
      },
      { from: "customer", text: "That works — let's do dragon chicken and 7up" },
      {
        from: "bot",
        text: "Lovely choice! Will this be **delivery**, **take away**, or **serve in car**?",
      },
      { from: "customer", text: "Delivery please — Johar Town block E" },
      {
        from: "bot",
        text: "Delivery fee is PKR 150, so you're at **PKR 1,090** before tax. Can I get your **name**, **phone number**, and whether you're paying **card** or **cash**?",
      },
      { from: "customer", text: "Sara, 0333 4455667, card" },
      {
        from: "bot",
        text: "Thanks Sara! ✅ Order confirmed — Dragon Chicken (full), 7up, delivery to Johar Town. Card total **PKR 1,144** incl. tax. ETA about **40 minutes**.",
      },
    ],
  },
  {
    id: "takeaway",
    label: "Take away",
    tag: "Pickup",
    tagColor: "bg-emerald-100 text-emerald-700 border-emerald-200",
    bubbles: [
      {
        from: "bot",
        text: `Hi! Welcome to **${RESTAURANT_NAME}**. What are you thinking of ordering today?`,
      },
      { from: "customer", text: "Do you have soup? Something light for two people" },
      {
        from: "bot",
        text: "**Chicken Corn Soup** is a favourite — **single** (PKR 550) or **full** (PKR 900). For two sharing lightly, two singles work well. We also have **Prawn on Toast** (PKR 750) if you want a bite on the side.",
      },
      { from: "customer", text: "2 chicken corn soup singles and 1 prawn on toast" },
      {
        from: "bot",
        text: "Got it — 2× Chicken Corn Soup (single) + Prawn on Toast = **PKR 1,850**. Is this for **delivery**, **take away**, or **serve in car**?",
      },
      { from: "customer", text: "Take away — I'll pick up in about 20 minutes" },
      { from: "bot", text: "Perfect. What **name** should I put on the order, and **card** or **cash**?" },
      { from: "customer", text: "Hassan, cash" },
      {
        from: "bot",
        text: "Thanks Hassan! ✅ Take-away order is in — total **PKR 2,146** (cash, 16% tax). Ready for pickup in roughly **20 minutes**.",
      },
    ],
  },
  {
    id: "serve-in-car",
    label: "Serve in car",
    tag: "Curbside",
    tagColor: "bg-amber-100 text-amber-800 border-amber-200",
    bubbles: [
      {
        from: "bot",
        text: `Welcome to **${RESTAURANT_NAME}**! 👋 Ordering from the car park, or dining in today?`,
      },
      { from: "customer", text: "I'm parked outside — what do you suggest for a quick bite?" },
      {
        from: "bot",
        text: "For something quick, **Beef Chilli Dry** (single, PKR 750) is fast from the kitchen. **Fresh Lime Soda** (PKR 150) pairs well if you want a drink. Interested?",
      },
      { from: "customer", text: "Beef chilli dry sounds good. I'm in a white Corolla near the entrance" },
      {
        from: "bot",
        text: "I'll set this as **serve in car**. Can you confirm your **spot number** so we find you easily?",
      },
      { from: "customer", text: "Spot 4" },
      { from: "bot", text: "Noted — **White Corolla, spot 4**. Want to add that lime soda, or anything else?" },
      { from: "customer", text: "Yes add the lime soda. That's everything" },
      {
        from: "bot",
        text: "Subtotal **PKR 900**. Your **name**, and **card** or **cash**?",
      },
      { from: "customer", text: "Omar, card" },
      {
        from: "bot",
        text: "Thanks Omar! ✅ We'll bring your order to **spot 4** — Beef Chilli Dry (single), Fresh Lime Soda. Card total **PKR 945** incl. tax. About **15 minutes**.",
      },
    ],
  },
];

const PAUSE_AFTER_DONE_MS = 4000;
const FADE_MS = 320;
const INPUT_CHAR_MS = 34;
const PAUSE_BEFORE_SEND_MS = 480;
const SEND_ANIM_MS = 280;

function botTypingDelayMs(text: string): number {
  const base = 1600;
  const perChar = 38;
  const cap = 5200;
  return Math.min(cap, Math.max(base, text.length * perChar)) + 500;
}

function MdText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return (
          <span key={i}>
            {part.split("\n").map((line, j, arr) => (
              <span key={j}>
                {line}
                {j < arr.length - 1 && <br />}
              </span>
            ))}
          </span>
        );
      })}
    </>
  );
}

function TypingBubble() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.2 }}
      className="flex justify-start"
    >
      <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold shrink-0 mr-2 mt-0.5 shadow-sm">
        {BOT_AVATAR}
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm flex gap-1.5 items-center">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="w-2 h-2 bg-brand-300 rounded-full"
            animate={{ y: [0, -5, 0], opacity: [0.45, 1, 0.45] }}
            transition={{
              duration: 0.55,
              repeat: Infinity,
              delay: i * 0.14,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </motion.div>
  );
}

function restartScenario(
  setFading: (v: boolean) => void,
  setPaused: (v: boolean) => void,
  setVisibleCount: (v: number) => void,
  setIsBotTyping: (v: boolean) => void,
  setInputDraft: (v: string) => void,
  setIsComposing: (v: boolean) => void,
  setSendPulse: (v: boolean) => void,
) {
  setFading(true);
  setPaused(true);
  setInputDraft("");
  setIsComposing(false);
  setSendPulse(false);
  window.setTimeout(() => {
    setVisibleCount(0);
    setIsBotTyping(true);
    setFading(false);
    setPaused(false);
  }, FADE_MS);
}

export function Demo() {
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [isBotTyping, setIsBotTyping] = useState(true);
  const [inputDraft, setInputDraft] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [sendPulse, setSendPulse] = useState(false);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);

  const chatRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scenario = SCENARIOS[scenarioIdx];
  const bubbles = scenario.bubbles.slice(0, visibleCount);
  const finished = visibleCount >= scenario.bubbles.length && !isBotTyping && !isComposing;

  const scrollToBottom = useCallback(() => {
    const container = chatRef.current;
    const bottom = bottomRef.current;
    if (!container || !bottom) return;
    const top = bottom.offsetTop - container.clientHeight + bottom.clientHeight + 16;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [visibleCount, isBotTyping, inputDraft, scenarioIdx, scrollToBottom]);

  /* ── playback: bot typing indicator OR customer types in input → send click ── */
  useEffect(() => {
    if (paused || fading) return;

    const all = scenario.bubbles;
    if (visibleCount >= all.length) {
      setIsBotTyping(false);
      setIsComposing(false);
      setInputDraft("");
      return;
    }

    const next = all[visibleCount];
    const timers: number[] = [];
    let intervalId: number | undefined;

    if (next.from === "bot") {
      setInputDraft("");
      setIsComposing(false);
      setSendPulse(false);
      setIsBotTyping(true);

      const t = window.setTimeout(() => {
        setIsBotTyping(false);
        setVisibleCount((c) => c + 1);
      }, botTypingDelayMs(next.text));
      timers.push(t);
    } else {
      setIsBotTyping(false);
      setIsComposing(true);
      setInputDraft("");

      const text = next.text;
      let i = 0;

      intervalId = window.setInterval(() => {
        i += 1;
        setInputDraft(text.slice(0, i));

        if (i >= text.length) {
          if (intervalId !== undefined) window.clearInterval(intervalId);

          const t1 = window.setTimeout(() => {
            setSendPulse(true);

            const t2 = window.setTimeout(() => {
              setSendPulse(false);
              setIsComposing(false);
              setInputDraft("");
              setVisibleCount((c) => c + 1);
            }, SEND_ANIM_MS);
            timers.push(t2);
          }, PAUSE_BEFORE_SEND_MS);
          timers.push(t1);
        }
      }, INPUT_CHAR_MS);
    }

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [visibleCount, scenario.bubbles, scenarioIdx, paused, fading]);

  useEffect(() => {
    if (!finished || paused || fading) return;
    const t = window.setTimeout(() => {
      restartScenario(
        setFading,
        setPaused,
        setVisibleCount,
        setIsBotTyping,
        setInputDraft,
        setIsComposing,
        setSendPulse,
      );
    }, PAUSE_AFTER_DONE_MS);
    return () => window.clearTimeout(t);
  }, [finished, paused, fading]);

  const selectScenario = (idx: number) => {
    if (idx === scenarioIdx) return;
    setFading(true);
    setPaused(true);
    setInputDraft("");
    setIsComposing(false);
    setSendPulse(false);
    window.setTimeout(() => {
      setScenarioIdx(idx);
      setVisibleCount(0);
      setIsBotTyping(true);
      setFading(false);
      setPaused(false);
    }, FADE_MS);
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <PublicNav />

      <section className="pt-28 pb-10 relative overflow-hidden bg-gradient-to-b from-brand-50/60 to-white">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 text-xs font-semibold text-brand-600 bg-brand-50 border border-brand-100 rounded-full px-4 py-1.5 mb-5"
          >
            Product demo
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight"
          >
            See Cravbot <span className="gradient-text">in action</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-3 text-gray-500 text-sm sm:text-base max-w-xl mx-auto"
          >
            How customers order — dine-in, delivery, pickup, and curbside.
          </motion.p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {SCENARIOS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => selectScenario(i)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  i === scenarioIdx
                    ? "bg-brand-500 text-white shadow-md shadow-brand-500/25"
                    : "bg-white text-gray-600 border border-gray-200 hover:border-brand-200 hover:text-brand-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-[1fr_300px] gap-6 lg:gap-8 items-start">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white border border-gray-100 rounded-2xl shadow-lg overflow-hidden"
            >
              <div className="flex items-center gap-3 px-5 py-4 bg-brand-500">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold text-white text-sm shrink-0 ring-2 ring-white/10">
                  {BOT_AVATAR}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm leading-tight">{RESTAURANT_NAME}</p>
                  <p className="text-orange-200 text-xs truncate">AI ordering assistant • online</p>
                </div>
              </div>

              <div className="bg-gray-50 border-b border-gray-100 px-4 py-2">
                <span
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${scenario.tagColor}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 animate-pulse" />
                  {scenario.tag}
                </span>
              </div>

              <div
                ref={chatRef}
                className={`h-[460px] overflow-y-auto overflow-x-hidden px-4 sm:px-5 py-4 space-y-3 bg-gradient-to-b from-gray-50 to-gray-100/80 scrollbar-modern transition-opacity duration-300 ${
                  fading ? "opacity-40" : "opacity-100"
                }`}
              >
                <AnimatePresence initial={false} mode="popLayout">
                  {bubbles.map((msg, i) => (
                    <motion.div
                      key={`${scenario.id}-${i}`}
                      layout
                      initial={{ opacity: 0, y: 10, scale: 0.94 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 420,
                        damping: 28,
                        mass: 0.8,
                      }}
                      className={`flex ${msg.from === "customer" ? "justify-end" : "justify-start"}`}
                    >
                      {msg.from === "bot" && (
                        <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold shrink-0 mr-2 mt-0.5 shadow-sm">
                          {BOT_AVATAR}
                        </div>
                      )}
                      <div
                        className={`max-w-[88%] sm:max-w-[82%] px-4 py-2.5 text-sm leading-relaxed ${
                          msg.from === "customer"
                            ? "bg-brand-500 text-white rounded-2xl rounded-br-md shadow-md shadow-brand-500/20"
                            : "bg-white text-gray-800 border border-gray-100/80 shadow-sm rounded-2xl rounded-bl-md"
                        }`}
                      >
                        <MdText text={msg.text} />
                      </div>
                    </motion.div>
                  ))}

                  {isBotTyping && !fading && visibleCount < scenario.bubbles.length && (
                    <TypingBubble key="typing" />
                  )}
                </AnimatePresence>
                <div ref={bottomRef} className="h-1" />
              </div>

              <div className="px-4 py-3 bg-white border-t border-gray-100 flex items-center gap-2">
                <div
                  className={`flex-1 rounded-full px-4 py-2.5 text-sm min-h-[42px] flex items-center transition-colors duration-200 ${
                    isBotTyping
                      ? "bg-gray-100/90"
                      : inputDraft
                        ? "bg-white border border-gray-200 shadow-sm"
                        : "bg-gray-100/90"
                  }`}
                >
                  {isBotTyping ? (
                    <span className="text-gray-400 select-none">Assistant is typing…</span>
                  ) : inputDraft || isComposing ? (
                    <span className="text-gray-900 truncate select-none">
                      {inputDraft}
                      {isComposing && (
                        <span className="inline-block w-0.5 h-[1.05em] bg-brand-500 ml-px align-[-0.1em] animate-pulse" />
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-400 select-none">Type a message…</span>
                  )}
                </div>
                <motion.button
                  type="button"
                  aria-label="Send message"
                  animate={
                    sendPulse
                      ? { scale: 0.86, backgroundColor: "#ea580c" }
                      : { scale: 1, backgroundColor: inputDraft ? "#f97316" : "#fdba74" }
                  }
                  transition={{ type: "spring", stiffness: 520, damping: 22 }}
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm shadow-brand-500/20"
                >
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </motion.button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white border border-gray-100 rounded-2xl shadow-lg p-6 lg:sticky lg:top-28"
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-gray-900">Book a walkthrough</h2>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                See Cravbot on <strong className="text-gray-700">your menu</strong> — with your branding, dashboard, and alerts.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-600">
                {SCENARIOS.map((s) => (
                  <li key={s.id} className="flex items-start gap-2">
                    <span className="text-brand-500 mt-0.5">✓</span>
                    {s.label}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact?purpose=demo"
                className="mt-6 flex w-full items-center justify-center gap-2 px-5 py-3 bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold rounded-xl btn-glow transition-colors"
              >
                Request a demo
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link
                to="/contact"
                className="mt-3 flex w-full items-center justify-center px-5 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-semibold rounded-xl transition-colors"
              >
                General enquiry
              </Link>
              <p className="mt-4 text-[11px] text-gray-400 text-center">Usually within 1 business day</p>
            </motion.div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
